import { useState } from 'react'

import { Button, QueryStatePanel, StatusMessage, TextField } from '@krp/ui-kit'

import { placeNameProblem } from '../model/labels'
import { PLACE_NAME_MAX } from '../model/types'
import { ErrorBlock, SrOnly } from './parts'
import type { PlaceFormActions, PlaceFormVm } from './screenTypes'
import './schedule.css'

function PlaceForm({ vm, actions }: { vm: PlaceFormVm; actions: PlaceFormActions }) {
  const [name, setName] = useState(vm.initialName)
  const [confirming, setConfirming] = useState(false)
  const busy = vm.saving || vm.archiving
  const problem = placeNameProblem(name)
  const unchanged = vm.mode === 'edit' && name.trim() === vm.initialName
  const reason = problem ?? (unchanged ? 'Название не менялось.' : null)

  return (
    <form
      className="sch-editor"
      aria-label={vm.mode === 'create' ? 'Новый объект' : `Объект: ${vm.initialName}`}
      onSubmit={(event) => {
        event.preventDefault()
        if (reason === null && !busy) actions.save(name.trim())
      }}
    >
      <TextField label="Название объекта" value={name} onChange={(value) => setName(value.slice(0, PLACE_NAME_MAX))} placeholder="Например, Волная 28" />
      <p className="sch-editor__hint">Название вольное: магазин, точка, адрес. Объект ни к чему не привязан, название не должно повторять другой объект.</p>
      {problem !== null && name !== '' && (
        <p className="sch-editor__problem" role="alert">
          {problem}
        </p>
      )}
      {vm.error !== null && <ErrorBlock error={vm.error} />}

      {confirming ? (
        <div className="sch-editor__confirm" role="group" aria-label="Убрать объект в архив">
          <p className="sch-editor__hint">Объект исчезнет из списка плиток. Назначенные кассиры в календаре сохранятся, но менять их станет нельзя.</p>
          <div className="sch-editor__buttons">
            <Button variant="danger" actionId="schedule.place-form.archive-confirm" busy={vm.archiving} disabled={vm.saving} onClick={actions.archive}>
              {vm.archiving ? 'Убирается…' : 'Да, убрать в архив'}
            </Button>
            <Button variant="ghost" actionId="schedule.place-form.archive-cancel" disabled={busy} onClick={() => setConfirming(false)}>
              Не убирать
            </Button>
          </div>
        </div>
      ) : (
        <div className="sch-editor__buttons">
          <Button type="submit" variant="primary" actionId="schedule.place-form.save" busy={vm.saving} disabled={reason !== null || vm.archiving} disabledReason={reason}>
            {vm.saving ? 'Сохраняется…' : vm.mode === 'create' ? 'Создать' : 'Сохранить'}
          </Button>
          {vm.mode === 'edit' && (
            <Button variant="danger" actionId="schedule.place-form.archive" disabled={busy} onClick={() => setConfirming(true)}>
              В архив
            </Button>
          )}
          <Button variant="ghost" actionId="schedule.place-form.close" disabled={busy} onClick={actions.close}>
            Закрыть
          </Button>
        </div>
      )}
    </form>
  )
}

/**
 * Окно «Новый объект» и «Изменить объект»: название и, при правке, «В архив» с подтверждением. Занятое название служба отклоняет
 * (409) — её текст показывается дословно и не исчезает сам. Без права править окно только объясняет, у кого оно есть.
 */
export function PlaceFormScreen({ vm, actions }: { vm: PlaceFormVm; actions: PlaceFormActions }) {
  if (vm.load.status === 'loading' || vm.load.status === 'idle')
    return (
      <div className="sch-skeleton" role="status">
        <SrOnly>Загружаем объект…</SrOnly>
        <span className="sch-skeleton__line" aria-hidden="true" />
      </div>
    )
  if (vm.load.status !== 'ready')
    return <QueryStatePanel query={vm.load} emptyText="" onRetry={actions.reload} retryActionId="schedule.place-form.reload" titles={{ error: 'Не удалось получить объект' }} />
  if (!vm.canManage) return <p>Создавать и менять объекты может тот, кому выдано право править график.</p>
  if (vm.notFound) return <StatusMessage kind="error" title="Объект не найден" text="Такого объекта нет: его могли убрать в архив." />
  return <PlaceForm vm={vm} actions={actions} />
}
